import {useMemo} from 'react';
import {type IntlShape} from 'react-intl';

import {ChannelType} from '@time-webkit/all/types/general';

interface GetCreateMessageProps {
    isThread: boolean;
    currentChannelType: ChannelType;
    currentChannelDisplayName: string;
    currentUserName?: string;
    formatMessage: IntlShape['formatMessage'];
}

export const useGetCreateMessage = ({
    isThread,
    currentChannelType,
    currentChannelDisplayName,
    currentUserName,
    formatMessage,
}: GetCreateMessageProps) => {
    return useMemo(() => {
        if (currentChannelType === ChannelType.DM) {
            return formatMessage({
                id: isThread ? 'create_post.replyDirect' : 'create_post.writeDirectOrGroup',
                defaultMessage:
                    isThread ? 'Reply to thread with {channelDisplayName}' : 'Write to {channelDisplayName}',
            }, {channelDisplayName: currentChannelDisplayName});
        }

        if (currentChannelType === ChannelType.GM) {
            const userNames = currentChannelDisplayName.split(', ').filter((userName) => userName !== currentUserName);
            let channelDisplayName;
            const and = formatMessage({id: 'create_post.and', defaultMessage: ' and '});
            if (userNames.length === 2) {
                channelDisplayName = userNames[0] + and + userNames[1];
            } else {
                const count = userNames.length - 3;
                const withMoreMembers = formatMessage({
                    id: isThread ? 'create_post.with_more_members.in_thread' : 'create_post.with_more_members',
                    defaultMessage: ' and {count} more {count, plural, one {member} other {members}}',
                }, {count});
                const [firstUser = '', secondUser = '', thirdUser = ''] = userNames;
                channelDisplayName = `${firstUser}, ${secondUser}${count ? ', ' : and}${thirdUser}${count ? withMoreMembers : ''}`;
            }

            return formatMessage({
                id: isThread ? 'create_post.replyGroup' : 'create_post.writeDirectOrGroup',
                defaultMessage:
                    isThread ? 'Reply to thread in group with {channelDisplayName}' : 'Write to {channelDisplayName}',
            }, {channelDisplayName});
        }

        return formatMessage({
            id: isThread ? 'create_post.reply' : 'create_post.write',
            defaultMessage: isThread ? 'Reply to thread in {channelDisplayName}' : 'Write to {channelDisplayName}',
        }, {channelDisplayName: currentChannelDisplayName});
    }, [currentChannelDisplayName, formatMessage, currentChannelType, isThread, currentUserName]);
};
